import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ServiceProvider } from '@/types';

// Transform database row (snake_case) to TypeScript (camelCase)
function transformProvider(row: Record<string, unknown>): ServiceProvider {
  return {
    id: row.id as string,
    userId: row.user_id as string | null,
    organizationId: row.organization_id as string | null,
    businessName: row.business_name as string,
    slug: row.slug as string,
    description: row.description as string | null,
    phone: row.phone as string | null,
    email: row.email as string | null,
    website: row.website as string | null,
    address: row.address as string | null,
    city: row.city as string | null,
    state: row.state as string | null,
    zipCode: row.zip_code as string | null,
    latitude: row.latitude as number | null,
    longitude: row.longitude as number | null,
    serviceTypes: (row.service_types || []) as ServiceProvider['serviceTypes'],
    specialties: (row.specialties as string[]) || [],
    serviceAreaMiles: (row.service_area_miles as number) || 25,
    isVerified: row.is_verified as boolean,
    isClaimed: row.is_claimed as boolean,
    logoUrl: row.logo_url as string | null,
    coverImageUrl: row.cover_image_url as string | null,
    galleryUrls: (row.gallery_urls as string[]) || [],
    hours: (row.hours as ServiceProvider['hours']) || {},
    certifications: (row.certifications as string[]) || [],
    yearsInBusiness: row.years_in_business as number | null,
    averageRating: row.average_rating as number | null,
    reviewCount: (row.review_count as number) || 0,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

async function fetchProvider(slugOrId: string): Promise<ServiceProvider | null> {
  // Try to find by slug first, then by id
  const { data, error } = await supabase
    .from('service_providers')
    .select('*')
    .or(`slug.eq.${slugOrId},id.eq.${slugOrId}`)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('Error fetching provider:', error);
    throw error;
  }

  if (!data) return null;

  // Fetch rating data using the database function
  const { data: ratingData } = await supabase
    .rpc('get_provider_rating', { provider_uuid: data.id });

  const provider = transformProvider(data);

  // Add computed rating data
  if (ratingData && ratingData.length > 0) {
    provider.averageRating = ratingData[0].average_rating;
    provider.reviewCount = Number(ratingData[0].review_count) || 0;
  }

  return provider;
}

export function useProvider(slugOrId: string) {
  return useQuery({
    queryKey: ['provider', slugOrId],
    queryFn: () => fetchProvider(slugOrId),
    enabled: !!slugOrId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
